import { parseFrontmatter } from 'components/knz/parser/frontmatter';
import { ArticleVisibility } from 'stores/ArticleStoreAdapter';

/**
 * 記事編集で knz 本文の FrontMatter を記事メタ（title / subtitle / description / author / visible）に同期するためのユーティリティ。
 *
 * - 本文コンテンツ（FrontMatter ブロック以降）は一切変更しない。
 * - FrontMatter の該当キーのみ入力値で差し替える。labels は FrontMatter 対象外（差し替えない）。
 * - `visible` は API の ArticleVisibility（PUBLIC / INTERNAL / PRIVATE）に対応する公開設定。
 * - `tech_knz.md` のパーサ準拠: 先頭 `---` 〜 閉じ `---` を FrontMatter ブロックとして扱う。
 */

export type FrontMatterMeta = {
  title: string;
  subtitle?: string;
  description?: string;
  /** 著者の userId 配列（1件以上） */
  authorUserIds: string[];
  /** 公開設定（ArticleVisibility に対応。FrontMatter の visible キーに書き出す） */
  visibility: ArticleVisibility;
};

/** FrontMatter の visible 値を ArticleVisibility に変換する（不正値は null）。 */
const parseVisible = (value: string | undefined): ArticleVisibility | null => {
  if (!value) return null;
  const upper = value.trim().toUpperCase();
  return upper === 'PUBLIC' || upper === 'INTERNAL' || upper === 'PRIVATE' ? upper : null;
};

/** 著者を frontmatter のリスト形式（`author:` + `- id`）でシリアライズする。 */
const serializeAuthors = (authorUserIds: string[]): string[] => {
  if (authorUserIds.length === 0) return ['author:'];
  if (authorUserIds.length === 1) return [`author: ${authorUserIds[0]}`];
  return ['author:', ...authorUserIds.map((id) => `- ${id}`)];
};

/**
 * knz ソースの FrontMatter ブロックを、指定メタで差し替えた新しいソースを返す。
 * 元ソースに FrontMatter が無い（先頭が `---` でない）場合は、先頭に新規 FrontMatter を付与する。
 *
 * 差し替え対象キー: title / subtitle / description / author / visible。
 * 元 FrontMatter の他キー（prose / breadcrumb 等）は保持する。
 */
export const syncKnzFrontMatter = (source: string, meta: FrontMatterMeta): string => {
  const lines = source.split('\n');
  const parsed = parseFrontmatter(lines);

  // 差し替え対象キー
  const managedKeys = new Set(['title', 'subtitle', 'description', 'author', 'visible']);

  // 新しい FrontMatter 行を組み立てる
  const managedLines: string[] = [`title: ${meta.title}`];
  if (meta.subtitle && meta.subtitle.trim() !== '') {
    managedLines.push(`subtitle: ${meta.subtitle}`);
  }
  if (meta.description && meta.description.trim() !== '') {
    managedLines.push(`description: ${meta.description}`);
  }
  managedLines.push(...serializeAuthors(meta.authorUserIds));
  // 公開設定（ArticleVisibility に対応）
  managedLines.push(`visible: ${meta.visibility}`);

  // 元ソースに FrontMatter が無い場合（bodyStartLine が 0 = 先頭が `---` でない）
  const hasFrontmatter = lines.length > 0 && lines[0].trim() === '---';
  if (!hasFrontmatter) {
    const body = source;
    return ['---', ...managedLines, '---', '', body].join('\n');
  }

  // 閉じ `---` の位置（bodyStartLine は閉じ `---` の次行 1-indexed）
  const closeLineIndex = parsed.bodyStartLine - 2; // 0-indexed の閉じ `---`
  const bodyStartIndex = parsed.bodyStartLine - 1; // 0-indexed の本文開始

  // 元 FrontMatter の中から、管理対象外のキー行（および著者以外）を保持する
  const preservedLines: string[] = [];
  for (let i = 1; i < closeLineIndex; i++) {
    const raw = lines[i];
    const trimmed = raw.trim();
    if (trimmed === '') continue;
    // リスト項目行（`- id` / ` - id`。インデント 0 個以上を許容）: 直前が author のリストならスキップ、そうでなければ保持
    const isListItem = /^\s*-/.test(raw);
    const idx = raw.indexOf(':');
    const key = idx === -1 ? '' : raw.slice(0, idx).trim();

    if (isListItem) {
      // 管理対象（author）のリスト項目は破棄。他キー配下のリストは保持。
      // ここでは簡易に、直前の非リスト行のキーを追跡する必要があるが、
      // 実用上 author 以外でリストを使うのは稀のため、author 管理下でなければ保持する。
      preservedLines.push(raw);
      continue;
    }
    if (idx === -1) {
      // key:value でない行（コメント等）はそのまま保持
      preservedLines.push(raw);
      continue;
    }
    if (managedKeys.has(key)) {
      // 管理対象キーは破棄（後で新しい値を入れる）。author の場合、続くリスト項目も破棄する。
      if (key === 'author') {
        // author: の直後に続くリスト行（インデント有無を問わず）を読み飛ばす
        let j = i + 1;
        while (j < closeLineIndex && /^\s*-/.test(lines[j])) {
          i = j;
          j++;
        }
      }
      continue;
    }
    preservedLines.push(raw);
  }

  const bodyLines = lines.slice(bodyStartIndex);
  return ['---', ...managedLines, ...preservedLines, '---', ...bodyLines].join('\n');
};

/**
 * knz ソースの FrontMatter から記事メタのプレフィル値を抽出する。
 * 新規登録画面で knz を選んだときに使用する。labels は対象外。
 * visibility は visible キーから抽出する（不正値/未指定は null）。
 */
export const extractKnzFrontMatterMeta = (
  source: string,
): {
  title: string;
  subtitle: string;
  description: string;
  authorUserIds: string[];
  visibility: ArticleVisibility | null;
} => {
  const lines = source.split('\n');
  const parsed = parseFrontmatter(lines);
  const fm = parsed.frontmatter;
  if (!fm) {
    return { title: '', subtitle: '', description: '', authorUserIds: [], visibility: null };
  }
  return {
    title: fm.title ?? '',
    subtitle: fm.extra.subtitle ?? '',
    description: fm.extra.description ?? '',
    authorUserIds: fm.authors ?? [],
    visibility: parseVisible(fm.extra.visible),
  };
};
